import axios from 'axios';
import type { HeadersDefaults, InternalAxiosRequestConfig } from 'axios';
import { API_URL } from '@/configs';
import { loadBranding } from '@/services/branding.service';

const http = axios.create({
  baseURL: API_URL,
});

/**
 * A brand is reached by path (`bioterapias.cl/<slug>/...`) and the server
 * resolves the tenant from that first segment, switching the connection to the
 * brand's schema for the whole request. A same-origin call written as
 * `/api/v1/professionals` resolves against the platform root instead, so the
 * middleware never sees the brand, the schema is never switched, and the request
 * is answered from the apex -- where another brand's professionals are the ones
 * that show up. Prefixing every same-origin call with the brand's `pathPrefix`
 * (the same value the SPA router uses as its history base) keeps the request
 * inside the brand it was made from.
 *
 * Registered on both the shared instance and the axios default because several
 * website modules import `axios` directly. It is a no-op on the apex and in the
 * panel, where there is no brand prefix.
 */
export function withTenantPrefix(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const prefix = loadBranding().pathPrefix;
  if (!prefix) return config;

  const url = config.url ?? '';
  if (!url || /^https?:\/\//i.test(url)) return config;
  if (url === prefix || url.startsWith(`${prefix}/`)) return config;

  config.url = url.startsWith('/') ? `${prefix}${url}` : `${prefix}/${url}`;
  return config;
}

http.interceptors.request.use(withTenantPrefix);
axios.interceptors.request.use(withTenantPrefix);

http.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // if (error.response?.status === 401) {
    //   localStorage.clear();
    //   window.location.href = '/login';
    //   return Promise.reject(error);
    // }

    // Extract error message from response data
    // Try to get the error object or message from the response
    const errorData = error?.response?.data;

    // If it's already an object with error/message, return it as is
    if (typeof errorData === 'object' && errorData !== null) {
      return Promise.reject(errorData);
    }

    // Otherwise return the error data string or a fallback
    const errMessage: string = errorData || 'Unknown Error';
    return Promise.reject(errMessage);
  },
);

const setHTTPHeader = (header: HeadersDefaults['common']) => {
  http.defaults.headers.common = { ...http.defaults.headers.common, ...header };
};

export { http, setHTTPHeader };
